import {useEffect, useMemo, useState} from 'react';
import {useFormContext} from "react-hook-form";
import {generateRelativeUrl, generateUrlFromRelativeUrl} from "v4/services/api.service";
import {debounce} from "v4/utils";

const CACHE_TTL = 30_000;
const CACHE_MAX_ENTRIES = 100;
const cache = new Map();

function getCachedPromise(url) {
    const entry = cache.get(url);
    if (!entry) return null;

    if (entry.isResolved && Date.now() - entry.at >= CACHE_TTL) {
        cache.delete(url);
        return null;
    }

    return entry.promise;
}

function fetchAutocompleteData(url) {
    const cachedPromise = getCachedPromise(url);
    if (cachedPromise) return cachedPromise;

    if (cache.size >= CACHE_MAX_ENTRIES) {
        cache.delete(cache.keys().next().value);
    }

    const entry = {at: Date.now(), isResolved: false, promise: null};
    entry.promise = fetch(url, {
        method: 'GET',
        headers: {Accept: 'application/ld+json'},
    })
        .then(res => res.ok ? res.json() : null)
        .then(json => {
            if (!json) {
                cache.delete(url);
                return null;
            }
            entry.at = Date.now();
            entry.isResolved = true;
            return json;
        })
        .catch(() => {
            cache.delete(url);
            return null;
        });

    cache.set(url, entry);
    return entry.promise;
}

export default function useAutocomplete(autoCompleteUrl, dataField, fieldValue, length = 3, autocompleteValues, target, targetField) {
    const valuesKey = autocompleteValues ?? 'hydra:member';
    const [resolvedValues, setResolvedValues] = useState(null);

    useEffect(() => {
        if (!autoCompleteUrl || !(fieldValue?.length >= length)) return;

        const generateUrlFunction = autoCompleteUrl.startsWith('http') ? generateRelativeUrl : generateUrlFromRelativeUrl;
        const url = generateUrlFunction(autoCompleteUrl, {[dataField]: fieldValue});

        let cancelled = false;
        debounce(() => fetchAutocompleteData(url).then(data => {
            if (!cancelled) setResolvedValues(data?.[valuesKey] ?? null);
        }));

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoCompleteUrl, dataField, fieldValue, length]);

    const {setValue} = useFormContext();
    useEffect(() => {
        if (autoCompleteUrl && fieldValue?.length >= length && target && targetField) {
            const targetData = resolvedValues?.filter(item => String(item[dataField]) === String(fieldValue));
            if (targetData?.length === 1) {
                setValue(target, targetData[0][targetField]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedValues, fieldValue]);

    return useMemo(() => {
        if (resolvedValues) {
            return resolvedValues.map(item => item[dataField]);
        }
        return [];
    }, [resolvedValues, dataField]);
}
